import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Typography,
  Button,
  StepLabel,
  Step,
  Stepper,
  Paper,
  Toolbar,
  Container,
  Box,
  AppBar,
  CssBaseline,
  CircularProgress,
  Link,
} from "@mui/material";
import ReCAPTCHA from "react-google-recaptcha";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation } from "react-router";
import { isObject } from "lodash";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, useStripe, useElements } from "@stripe/react-stripe-js";
import { v4 as uuidv4 } from "uuid";
import BookingQrCode from "../Common/BookingQrCode";
import AddressForm from "./AddressForm";
import Review from "./Review";
import DateTimeWrapper from "./DateTimeWrapper";
import { setUserData, setStoreData as setStore } from "../redux/action";
import { getAPI } from "../api/callAPI";
import NotFound from "../Common/NotFound";
import { checkMultiStore, isMobileOtpEnabled } from "../Common/utils";
import { LOCALSTORAGE_REF_KEY, LOCALSTORAGE_VIA_KEY } from "../Common/RefTracker";
import { isValidPhoneNumber } from "../Common/PhoneNumberInput";
import { useThemeColor } from "../hooks/useThemeColor";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import {
  getStripeClientSecret,
  getPublishableKeyFromPrepareData,
  isRazorpayGateway,
  isStripeGateway,
  UnsupportedGatewayError,
} from "../services/paymentGateway";
import {
  confirmUnifiedBooking,
  needsBookingPayment,
  payWithGatewayForBooking,
  prepareUnifiedBooking,
} from "../hooks/useUnifiedBooking";

const steps = ["Time Slots", "Customer details", "Review your Booking"];
let cachedStripePromise = null;
let cachedStripeKey = null;

function getStripePromise(publishableKey) {
  if (!publishableKey) return null;
  if (cachedStripePromise && cachedStripeKey === publishableKey) {
    return cachedStripePromise;
  }
  cachedStripeKey = publishableKey;
  cachedStripePromise = loadStripe(publishableKey);
  return cachedStripePromise;
}

function StripeActionButtonInner({ label, disabled, onActivate }) {
  const stripe = useStripe();
  const elements = useElements();

  return (
    <Button
      variant="contained"
      onClick={() => onActivate({ stripe, elements })}
      sx={{ ml: { sm: 1 } }}
      size="large"
      disabled={disabled}
    >
      {label}
    </Button>
  );
}

export default function Checkout() {
  useDocumentTitle("Book a Table");
  const { themeColor } = useThemeColor();
  const { storeSlug } = useParams();
  const location = useLocation();
  // const storeID = useMemo(() => {
  //   if (location.pathname) {
  //     return location.pathname.replace("/store/", "");
  //   }
  //   return null;
  // }, [location.pathname]);

  // Capture optional referral parameters from URL (apply only to standard booking)
  const { refParam, viaParam } = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return {
      refParam: params.get("ref"),
      viaParam: params.get("via"),
    };
  }, [location.search]);

  // Read last-seen referral values from localStorage
  const { storedRef, storedVia } = useMemo(() => {
    try {
      return {
        storedRef:
          typeof window !== "undefined"
            ? window.localStorage.getItem(LOCALSTORAGE_REF_KEY)
            : null,
        storedVia:
          typeof window !== "undefined"
            ? window.localStorage.getItem(LOCALSTORAGE_VIA_KEY)
            : null,
      };
    } catch (e) {
      return { storedRef: null, storedVia: null };
    }
  }, []);

  const liveEvent = useMemo(() => {
    if (location.state && location.state.liveEvent) {
      return location.state.liveEvent;
    }
    return null;
  }, [location.state]);
  // console.log("storeID", storeID);
  // console.log("liveEvent", liveEvent);
  const dispatch = useDispatch();
  const recaptchaRef = useRef(null);
  const st = useSelector((state) => state.auth);
  const [activeStep, setActiveStep] = useState(0);
  const [nextActive, setNextActive] = useState(false);
  const [error, setError] = useState("");
  const [orderNo, setOrderNo] = useState(0);

  const [apiLoading, setApiLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  let orderId = 0;
  const [storeLoading, setStoreLoading] = useState(true);
  const [storeData, setStoreData] = useState(null);
  const [storeConfig, setStoreConfig] = useState(null);
  const [captchaSuccess, setCaptchaSuccess] = useState(false);
  const [captchaError, setCaptchaError] = useState(null);
  const homePage = "/";

  const [advancePaymentData, setAdvancePaymentData] = useState(null);
  const [loadingAdvanceConfig, setLoadingAdvanceConfig] = useState(false);
  const [stripePublishableKey, setStripePublishableKey] = useState("");
  const [idempotencyKey] = useState(() => uuidv4());
  const idempotencyKeyRef = useRef(idempotencyKey);
  const paymentResultRef = useRef(null);
  const attemptSignatureRef = useRef("");
  const paidSignatureRef = useRef("");
  const [stripeReady, setStripeReady] = useState(false);
  const [confirmBookingData, setConfirmBookingData] = useState(null);

  const [isPhoneVerified, setIsPhoneVerified] = useState(false);

  const addressForm = useRef(null);

  const storeID = useMemo(() => {
    return storeData?.id;
  }, [storeData]);

  const onChangeCaptcha = (token) => {
    if (token) {
      setCaptchaSuccess(true);
    }
  };

  const checkStore = useCallback(
    async (endpoint) => {
      if (storeSlug) {
        dispatch(setStore({}));
        setStoreLoading(true);
        try {
         const resp = await getAPI(endpoint, `stores/${storeSlug}`);
          // console.log("resp >>> ", resp);
          dispatch(setStore(resp?.data["0"]));
          setStoreData(resp?.data["0"]);
          
          const store = resp?.data["0"];
          if (store) {
            setStoreConfig({
              otp_enable: store.otp_enable,
            });
          }
        } catch (error) {
          console.error("checkStore error >>> ", error);
          dispatch(setStore({}));
          setStoreData(null);
          setError("Store Not Found");
          setStoreConfig({ otp_enable: true });
        }
        setStoreLoading(false);
      } else {
        setStoreData(null);
        dispatch(setStore({}));
        setError("Store Not Found");
        setStoreLoading(false);
        setStoreConfig({ otp_enable: true });
      }
    },
    [storeSlug, dispatch]
  );

  // useEffect(() => {
  //   if (Object.keys(storeData).length) {
  //     dispatch(setStoreData(storeData));
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [storeData]);

  // useEffect(() => {
  //   if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
  //     checkStore(st?.endpoint);
  //     dispatch(setEndPoint(st?.endpoint));
  //   } else {
  //     const parts = window.location.host.split(".");
  //     if (parts.length >= 3) {
  //       dispatch(setEndPoint(parts[0]));
  //       checkStore(parts[0]);
  //     }
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  useEffect(() => {
    checkStore(st?.endpoint);
  }, [checkStore, st?.endpoint]);

  const buildBookingPayload = useCallback(() => {
    const userData = st?.userdata;
    const rawTime = String(userData?.Time || "").split("-")[0].trim();
    const timeWithSeconds = /^\d{1,2}:\d{2}:\d{2}$/.test(rawTime)
      ? rawTime
      : `${rawTime}:00`;
    const data = {
      store_id: st?.store?.id,
      booking_datetime: userData?.Date + " " + timeWithSeconds,
      adult: userData?.Adults,
      child: userData?.Child,
      infants: userData?.Infants,
      first_name: userData?.FirstName,
      last_name: userData?.LastName,
      email: userData?.Email,
      phone: userData?.Phone,
      comment: userData?.Requests,
      seating_preferences: userData?.SeatingPreference || "",
    };

    if (refParam != null) {
      data.reference = refParam;
    } else if (storedRef != null) {
      data.reference = storedRef;
    }

    if (viaParam != null) {
      data.via = viaParam;
    } else if (storedVia != null) {
      data.via = storedVia;
    } else {
      data.via = "3";
    }

    if (liveEvent) {
      data.upcoming_event_id = liveEvent?.id;
    }

    return data;
  }, [st, refParam, storedRef, viaParam, storedVia, liveEvent]);

  const resolvePublishableKey = useCallback(
    async (gatewayData) => {
      const fromPrepare = getPublishableKeyFromPrepareData(gatewayData);
      if (fromPrepare) return fromPrepare;

      const fromEnv = process.env.REACT_APP_STRIPE_KEY;
      if (fromEnv) return fromEnv;

      try {
        const resp = await getAPI(
          st?.endpoint,
          `get-store-payment-config/${st?.store?.id}`
        );
        return resp?.data?.public_payment_key || resp?.public_payment_key || "";
      } catch (err) {
        console.log("Error fetching payment config:", err);
        return "";
      }
    },
    [st?.endpoint, st?.store?.id]
  );

  const handlePhoneVerified = useCallback((value = true) => {
    setIsPhoneVerified(Boolean(value));
  }, []);

  // Re-evaluate Next when phone verification status changes on customer step.
  // AddressForm updates isPhoneVerified without rewriting form Data, so handleDataChange alone won't enable Next.
  useEffect(() => {
    if (activeStep !== 1) return;

    const data = st?.userdata || {};
    const isOtpEnabled = isMobileOtpEnabled(storeConfig?.otp_enable);
    
    let hasBasics = Boolean(data.FirstName) && Boolean(data.isAgree);
    
    if (isOtpEnabled) {
      hasBasics = hasBasics && 
        Boolean(data.Phone) && 
        isValidPhoneNumber(data.Phone || "");
    } else {
      hasBasics = hasBasics && 
        Boolean(data.Email) && 
        data.Email.includes("@");
    }

    if (hasBasics && isPhoneVerified) {
      setError("");
      setNextActive(true);
    } else if (activeStep === 1) {
      setNextActive(false);
    }
  }, [activeStep, isPhoneVerified, st?.userdata, storeConfig]);

  // const handleEmailVerified = useCallback(() => {
  //   setIsEmailVerified(true);
  // }, []);

  const bookingSignature = useCallback(() => {
    const userData = st?.userdata || {};
    return [
      userData.Date || "",
      userData.Time || "",
      userData.Adults || 0,
      userData.Child || 0,
      userData.Infants || 0,
      userData.SeatingPreference || "",
    ].join("|");
  }, [st?.userdata]);

  const callUnifiedPrepare = useCallback(async () => {
    setLoadingAdvanceConfig(true);
    setAdvancePaymentData(null);
    try {
      if (!isPhoneVerified) {
        throw new Error("Please verify your phone number first");
      }

      const token = localStorage.getItem("booking_auth_token");
      if (!token) {
        throw new Error("We couldn’t sign you in. Please request a new code.");
      }

      const signature = bookingSignature();
      if (paymentResultRef.current) {
        if (signature !== paidSignatureRef.current) {
          throw new Error(
            "Your card is already on hold for the previous booking. Confirm that request, or refresh the page to start again."
          );
        }
        return advancePaymentData;
      }

      if (signature !== attemptSignatureRef.current) {
        attemptSignatureRef.current = signature;
        idempotencyKeyRef.current = uuidv4();
      }

      const booking = buildBookingPayload();
      const prepareData = await prepareUnifiedBooking(
        st?.endpoint,
        token,
        booking,
        idempotencyKeyRef.current
      );

      setAdvancePaymentData(prepareData);

      if (needsBookingPayment(prepareData) && isStripeGateway(prepareData?.gateway?.gateway_name)) {
        const key = await resolvePublishableKey(prepareData?.gateway);
        setStripePublishableKey(key);
      }
      return prepareData;
    } catch (err) {
      console.log("unified/prepare error:", err);
      setAdvancePaymentData(null);
      setError(
        err?.response?.data?.message ||
          err.message ||
          "We couldn’t check the deposit. Please try again."
      );
    } finally {
      setLoadingAdvanceConfig(false);
    }
    return null;
  }, [
    buildBookingPayload,
    bookingSignature,
    advancePaymentData,
    st?.endpoint,
    resolvePublishableKey,
    isPhoneVerified,
    idempotencyKey,
  ]);

  const handleBookingConfirmed = (confirmData) => {
    const bookingId =
      confirmData?.booking_id || confirmData?.booking?.id || confirmData?.id;
    if (!bookingId) {
      setError("Booking was confirmed but no booking ID was returned. Please contact the venue.");
      return;
    }
    const raw = confirmData?.booking || confirmData;
    setConfirmBookingData({
      ...raw,
      id: raw?.id ?? raw?.booking_id ?? bookingId,
      store_id: raw?.store_id ?? raw?.store?.id ?? st?.store?.id,
    });
    setActiveStep(3);
    setOrderNo(bookingId);
  };

  const confirmDirectBooking = async () => {
    setApiLoading(true);
    setError("");
    try {
      if (!isPhoneVerified) {
        throw new Error("Please verify your phone number first");
      }

      const token = localStorage.getItem("booking_auth_token");
      if (!token) {
        throw new Error("We couldn’t sign you in. Please request a new code.");
      }

      const booking = buildBookingPayload();
      const confirmData = await confirmUnifiedBooking(
        st?.endpoint,
        token,
        idempotencyKeyRef.current,
        booking
      );
      handleBookingConfirmed(confirmData);
    } catch (err) {
      console.error("confirmDirectBooking error:", err);
      setError(
        err?.response?.data?.message ||
          err.message ||
          "We couldn’t complete your booking. Please try again."
      );
    } finally {
      setApiLoading(false);
    }
  };

  const payThenConfirm = async ({ stripe, elements } = {}) => {
    setApiLoading(true);
    setError("");
    try {
      if (!isPhoneVerified) {
        throw new Error("Please verify your phone number first");
      }

      const token = localStorage.getItem("booking_auth_token");
      if (!token) {
        throw new Error("We couldn’t sign you in. Please request a new code.");
      }

      const gateway = advancePaymentData?.gateway;
      if (!gateway?.gateway_name) {
        throw new Error("Payment gateway details are missing. Please go back and try again.");
      }

      const booking = buildBookingPayload();
      let paymentResult = paymentResultRef.current;
      if (!paymentResult) {
        const clientSecret = getStripeClientSecret(gateway, advancePaymentData);
        paymentResult = await payWithGatewayForBooking(gateway, {
          stripe,
          elements,
          clientSecret,
          prepareData: advancePaymentData,
        });
        paymentResultRef.current = paymentResult;
        paidSignatureRef.current = bookingSignature();
      }

      const confirmData = await confirmUnifiedBooking(
        st?.endpoint,
        token,
        idempotencyKeyRef.current,
        booking,
        gateway,
        paymentResult
      );
      handleBookingConfirmed(confirmData);
    } catch (err) {
      console.log("payThenConfirm error:", err);
      if (err instanceof UnsupportedGatewayError) {
        setError(err.message);
      } else {
        setError(
          paymentResultRef.current
            ? "Your card is already on hold. Try again to confirm. This will not place another hold."
            : err?.response?.data?.message ||
              err.message ||
              "We couldn’t complete your booking. Please try again."
        );
      }
    } finally {
      setApiLoading(false);
    }
  };

  const handleNext = async () => {
      if (activeStep === 2) {
        if (!process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY) {
          setCaptchaError("Verification could not load. Please refresh the page.");
          return;
        }
        if (!captchaSuccess) {
          setCaptchaError("Please complete the verification.");
          return;
        }
        return;
      } else if (activeStep < 1) {
      setNextActive(false);
      setActiveStep(activeStep + 1);
    } else {
      if (!isPhoneVerified) {
        setError("Please verify your phone number first");
        return;
      }
      const prepared = await callUnifiedPrepare();
      if (!prepared) {
        return;
      }
      setActiveStep(activeStep + 1);
    }
    if (activeStep !== 2) {
      setCaptchaSuccess(false);
      setCaptchaError(null);
    }
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const handleDataChange = (Data) => {
    if (activeStep === 0) {
      dispatch(setUserData(Data));
      if (
        Data.Time !== "" &&
        Data.Date !== "" &&
        (Number(Data.Adults) || 0) + (Number(Data.Child) || 0) + (Number(Data.Infants) || 0) > 0 &&
        Data.SeatingPreference !== "" && 
        Data.isSlotAvailable !== false
      )
        setNextActive(true);
      else setNextActive(false);
    }
    if (activeStep === 1) {
      // Always sync customer details so OTP verify can re-enable Next from latest data
      dispatch(setUserData(Data));

      const isOtpEnabled = isMobileOtpEnabled(storeConfig?.otp_enable);
      
      let Err = "";
      
      if (!Data.FirstName) {
        Err = "";
        setNextActive(false);
        setError(Err);
        return;
      }
      
      if (isOtpEnabled) {
      if (!Data.Phone || !isValidPhoneNumber(Data.Phone || "")) {
        Err = Data.Phone ? "Please Enter a Valid Phone Number" : "";
        setError(Err);
        setNextActive(false);
        return;
      }
      } else {
        if (!Data.Email || !Data.Email.includes("@")) {
          Err = "Please Enter a Valid Email Address";
          setError(Err);
          setNextActive(false);
          return;
        }
      }
      
      if (!Data.isAgree) {
        Err =
          "You must agree to terms & conditions and privacy policy in order to proceed";
        setError(Err);
        setNextActive(false);
        return;
      }

      if (isPhoneVerified) {
        setError("");
        setNextActive(true);
      } else {
        setError("");
        setNextActive(false);
      }
    }
  };

  function getStepContent(step) {
    switch (step) {
      case 0:
        return (
          <DateTimeWrapper
            handleData={handleDataChange}
            storeID={storeID}
            liveEvent={liveEvent}
          />
        );
      case 1:
        return (
          <AddressForm
            handleData={handleDataChange}
            ref={addressForm}
            onPhoneVerified={handlePhoneVerified}
            storeConfig={storeConfig}
          />
        );
      case 2:
        return (
          <Review
            advancePaymentData={advancePaymentData}
            loadingAdvanceConfig={loadingAdvanceConfig}
            onStripeReady={() => setStripeReady(true)}
          />
        );
      default:
        throw new Error("Unknown step");
    }
  }

  const needsAdvancePayment = needsBookingPayment(advancePaymentData);
  const gatewayName = advancePaymentData?.gateway?.gateway_name;
  const usesStripePayment =
    needsAdvancePayment && isStripeGateway(gatewayName);
  const usesRazorpayPayment =
    needsAdvancePayment && isRazorpayGateway(gatewayName);
  const clientSecret = usesStripePayment
    ? getStripeClientSecret(advancePaymentData?.gateway, advancePaymentData)
    : "";
  const stripePromise = usesStripePayment
    ? getStripePromise(stripePublishableKey)
    : null;

  if (storeLoading) {
    return (
      <Container maxWidth="lg">
        <CircularProgress variant="indeterminate" />
      </Container>
    );
  }

  if (!isObject(storeData) || Object.keys(storeData).length === 0) {
    return <NotFound message="This venue link is not valid." />;
  }

  const isReviewStep = activeStep === steps.length - 1;
  const depositAmount =
    advancePaymentData?.deposit_summary?.authorized_amount || 0;
  const depositCurrency = advancePaymentData?.deposit_summary?.currency || advancePaymentData?.gateway?.currency || "";
  const currencySymbol = depositCurrency === "GBP" ? "£" : depositCurrency === "USD" ? "$" : depositCurrency === "EUR" ? "€" : depositCurrency === "INR" ? "₹" : st?.currencySymbol || "₹";

  const actionLabel = apiLoading
    ? "Processing..."
    : !isReviewStep
    ? "Next"
    : needsAdvancePayment
    ? `Place card hold · ${currencySymbol}${depositAmount}`
    : "Request table";

  const actionDisabled =
    !nextActive ||
    apiLoading ||
    (isReviewStep &&
      usesStripePayment &&
      (loadingAdvanceConfig || !stripeReady)) ||
    (isReviewStep && usesRazorpayPayment && loadingAdvanceConfig);

  const handlePlainButtonClick = async () => {
    if (!isReviewStep) {
      handleNext();
      return;
    }
    if (!captchaSiteKey) {
      setCaptchaError("Verification could not load. Please refresh the page.");
      return;
    }
    if (!captchaSuccess) {
      setCaptchaError("Please complete the verification.");
      return;
    }
    await confirmDirectBooking();
  };

  const handleStripeButtonClick = async ({ stripe, elements }) => {
    if (!isReviewStep) {
      handleNext();
      return;
    }
    if (!captchaSiteKey) {
      setCaptchaError("Verification could not load. Please refresh the page.");
      return;
    }
    if (!captchaSuccess) {
      setCaptchaError("Please complete the verification.");
      return;
    }
    await payThenConfirm({ stripe, elements });
  };

  const usesStripeOnThisStep =
    usesStripePayment && isReviewStep && stripePromise && clientSecret;

  const isCompletionStep = activeStep === steps.length;

  const confirmedCurrencySymbol = confirmBookingData?.store?.currency_symbol || "£";
  const confirmedDepositAmount = confirmBookingData?.booking_payment?.authorized_amount || 0;
  const confirmedTotalGuests =
    (confirmBookingData?.adult || 0) +
    (confirmBookingData?.child || 0) +
    (confirmBookingData?.infants || 0);

  const captchaSiteKey = process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY;

  const nextHint = (() => {
    if (nextActive || isReviewStep) return "";
    const data = st?.userdata || {};
    if (activeStep === 0) {
      const total =
        (Number(data.Adults) || 0) +
        (Number(data.Child) || 0) +
        (Number(data.Infants) || 0);
      if (!data.Date) return "Choose a date to continue.";
      if (!data.Time) return "Choose a time to continue.";
      if (total < 1) return "Add at least one guest to continue.";
      if (!data.SeatingPreference) return "Choose a seating area to continue.";
      if (data.isSlotAvailable === false) return "This time isn’t open for your party. Choose another time to continue.";
    }
    if (activeStep === 1) {
      if (!data.FirstName) return "Enter your first name to continue.";
      if (!data.isAgree) return "Agree to the terms to continue.";
      if (!isPhoneVerified) return "Verify the code we sent you to continue.";
    }
    return "";
  })();

  const actionButtons = (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column-reverse", sm: "row" },
        justifyContent: activeStep ? "space-between" : "flex-end",
        alignItems: { xs: "stretch", sm: "center" },
        gap: { xs: 1, sm: 0 },
        mt: 3,
      }}
    >
      {activeStep !== 0 && (
        <Button onClick={handleBack} size="large" sx={{ ml: { sm: 1 } }}>
          Back
        </Button>
      )}

      {usesRazorpayPayment && isReviewStep ? (
        <Button
          variant="contained"
          onClick={() => payThenConfirm()}
          size="large"
          disabled={actionDisabled}
          sx={{ ml: { sm: 1 } }}
        >
          {actionLabel}
        </Button>
      ) : needsAdvancePayment && isReviewStep && !usesStripeOnThisStep ? (
        <Button
          variant="contained"
          size="large"
          disabled={true}
          sx={{ ml: { sm: 1 } }}
        >
          {loadingAdvanceConfig ? "Loading..." : actionLabel}
        </Button>
      ) : (
        <Button
          variant="contained"
          onClick={handlePlainButtonClick}
          size="large"
          disabled={actionDisabled}
          sx={{ ml: { sm: 1 } }}
        >
          {actionLabel}
        </Button>
      )}
    </Box>
  );

  const stepContent = isCompletionStep ? null : (
    <>
      {getStepContent(activeStep)}

      {isReviewStep ? (
        <>
          <Box sx={{ overflowX: "auto", maxWidth: "100%", mt: 2 }}>
            {captchaSiteKey ? (
            <ReCAPTCHA
              key="captcha-verification"
              ref={recaptchaRef}
              sitekey={captchaSiteKey}
              onChange={onChangeCaptcha}
            />
            ) : (
              <Typography color="error.main">
                Verification could not load. Please refresh the page.
              </Typography>
            )}
          </Box>
          {captchaError && (
            <Typography key="captcha-error" variant="subtitle1" color="error.main">
              {captchaError}
            </Typography>
          )}
        </>
      ) : null}

      {nextHint ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {nextHint}
        </Typography>
      ) : null}
      <Typography variant="subtitle1" color="error.main">
        {error}
      </Typography>
    </>
  );

  const storeAddress = [
    st?.store?.address_line_1,
    st?.store?.address_line_2,
    st?.store?.city,
    st?.store?.postcode,
  ]
    .map((part) => `${part || ""}`.trim())
    .filter(Boolean)
    .join(", ");
  const storePhone = `${st?.store?.phone || ""}`.trim();
  const storeEmail = `${st?.store?.email || ""}`.trim();

  return (
    <>
      <CssBaseline />
      <AppBar
        position="absolute"
        color="default"
        elevation={0}
        sx={{
          position: "relative",
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
        }}
      >
        <Toolbar
          sx={{
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            px: { xs: 1.5, sm: 2 },
            py: 1,
            gap: 0.25,
            minHeight: { xs: 56, sm: 72 },
          }}
        >
          <Typography
            variant="h6"
            color={checkMultiStore(st?.endpoint) ? themeColor : "#650033"}
            sx={{
              fontSize: { xs: "0.95rem", sm: "1.25rem" },
              textAlign: "center",
              lineHeight: 1.3,
              px: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
            }}
          >
            {st?.store?.name}
          </Typography>
          {storeAddress ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                textAlign: "center",
                lineHeight: 1.4,
                maxWidth: "100%",
                whiteSpace: "normal",
              }}
            >
              {storeAddress}
            </Typography>
          ) : null}
          {storePhone || storeEmail ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textAlign: "center", lineHeight: 1.4 }}
            >
              {storePhone ? (
                <Link
                  href={`tel:${storePhone.replace(/[^\d+]/g, "")}`}
                  color="inherit"
                  underline="hover"
                >
                  {storePhone}
                </Link>
              ) : null}
              {storePhone && storeEmail ? " · " : null}
              {storeEmail ? (
                <Link href={`mailto:${storeEmail}`} color="inherit" underline="hover">
                  {storeEmail}
                </Link>
              ) : null}
            </Typography>
          ) : null}
        </Toolbar>
      </AppBar>

      <Container
        component="main"
        maxWidth="sm"
        sx={{ mb: 4, px: { xs: 1.5, sm: 2 } }}
      >
        <Paper
          variant="outlined"
          sx={{
            my: { xs: 2, md: 6 },
            p: { xs: 2, sm: 2.5, md: 3 },
            overflow: "hidden",
          }}
        >
          <Typography
            component="h1"
            variant="h4"
            align="center"
            sx={{ fontSize: { xs: "1.5rem", sm: "2.125rem" } }}
          >
            Book Table
          </Typography>
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{
              pt: { xs: 2, sm: 3 },
              pb: { xs: 3, sm: 5 },
              "& .MuiStepLabel-label": {
                fontSize: { xs: "0.65rem", sm: "0.875rem" },
                mt: { xs: 0.5, sm: 1 },
                lineHeight: 1.2,
              },
              "& .MuiStepLabel-labelContainer": {
                maxWidth: { xs: 88, sm: "none" },
              },
              "& .MuiStepConnector-root": {
                top: { xs: 10, sm: 12 },
              },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {isCompletionStep ? (
            <>
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Box sx={{
                  width: 64, height: 64, borderRadius: "50%",
                  backgroundColor: "#DCFCE7", border: "2px solid #94febb",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  margin: "0 auto 16px",
                }}>
                  <Typography fontSize="28px">✓</Typography>
                </Box>
                <Typography variant="h5" gutterBottom fontWeight={600}>
                  {orderNo ? "Request sent" : ""}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Booking ID: #{orderNo}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  The venue still needs to accept this request.
                </Typography>
              </Box>

              <Box sx={{ border: "1px solid #E5E7EB", borderRadius: 2, p: 2, mb: 2 }}>
                <Typography fontWeight={600} fontSize="15px" mb={1.5}>
                  Reservation Details
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography fontSize="13px" color="text.secondary">Date & Time</Typography>
                  <Typography fontSize="13px" fontWeight={600}>
                    {confirmBookingData?.booking_datetime
                      ? new Date(confirmBookingData.booking_datetime.replace(" ", "T"))
                          .toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })
                      : ""}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography fontSize="13px" color="text.secondary">Guests</Typography>
                  <Typography fontSize="13px" fontWeight={600}>
                    {confirmedTotalGuests} {confirmedTotalGuests === 1 ? "Person" : "People"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography fontSize="13px" color="text.secondary">Seating</Typography>
                  <Typography fontSize="13px" fontWeight={600}>
                    {confirmBookingData?.seating_preferences || "Standard"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography fontSize="13px" color="text.secondary">Name</Typography>
                  <Typography fontSize="13px" fontWeight={600}>
                    {confirmBookingData?.first_name} {confirmBookingData?.last_name}
                  </Typography>
                </Box>
                {confirmBookingData?.phone && (
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography fontSize="13px" color="text.secondary">Phone</Typography>
                  <Typography fontSize="13px" fontWeight={600}>
                    {confirmBookingData?.phone}
                  </Typography>
                </Box>
                )}
                {confirmBookingData?.email && (
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography fontSize="13px" color="text.secondary">Email</Typography>
                    <Typography fontSize="13px" fontWeight={600}>
                      {confirmBookingData?.email}
                    </Typography>
                  </Box>
                )}
              </Box>

              {confirmBookingData?.booking_payment && (
                <Box sx={{ border: "1px solid #E5E7EB", borderRadius: 2, p: 2, mb: 2 }}>
                  <Typography fontWeight={600} fontSize="15px" mb={1.5}>
                    Security Deposit
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography fontSize="13px" color="text.secondary">Amount</Typography>
                    <Typography fontSize="13px" fontWeight={600}>
                      {confirmedCurrencySymbol}{confirmedDepositAmount}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography fontSize="13px" color="text.secondary">Status</Typography>
                    <Box sx={{ backgroundColor: "#FEF3C7", px: 1.5, py: 0.5, borderRadius: 10 }}>
                      <Typography fontSize="12px" fontWeight={600} color="#D97706">
                        Hold
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {orderNo && confirmBookingData && (
                <Box sx={{ border: "1px solid #E5E7EB", borderRadius: 2, p: 2, mb: 2, textAlign: "center" }}>
                  <Typography fontWeight={600} fontSize="15px" mb={1.5}>
                    Booking QR Code
                  </Typography>
                  <BookingQrCode
                    booking={confirmBookingData}
                    customerId={confirmBookingData?.customer_id}
                    appConfigName={st?.endpoint}
                    size={160}
                    style={{ width: "160px" }}
                  />
                  <Typography fontSize="12px" color="text.secondary" mt={1}>
                    Show this at the restaurant
                  </Typography>
                </Box>
              )}

              <Typography variant="body2" color="text.secondary" textAlign="center" mb={2}>
                {confirmBookingData?.email
                  ? `We have emailed this request to ${confirmBookingData.email}. We’ll also update you when the venue accepts it.`
                  : "We’ll send updates to the phone number you verified, and again when the venue accepts the request."}
              </Typography>

              <a href={homePage}>
                <Button variant="outlined" fullWidth>Back to Home</Button>
              </a>
            </>
          ) : usesStripeOnThisStep ? (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              {stepContent}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column-reverse", sm: "row" },
                  justifyContent: activeStep ? "space-between" : "flex-end",
                  alignItems: { xs: "stretch", sm: "center" },
                  gap: { xs: 1, sm: 0 },
                  mt: 3,
                }}
              >
                {activeStep !== 0 && (
                  <Button onClick={handleBack} size="large" sx={{ ml: { sm: 1 } }}>
                    Back
                  </Button>
                )}
                <StripeActionButtonInner
                  label={actionLabel}
                  disabled={actionDisabled}
                  onActivate={handleStripeButtonClick}
                />
              </Box>
            </Elements>
          ) : (
            <>
              {stepContent}
              {actionButtons}
            </>
          )}
        </Paper>
        {/* <Copyright /> */}
      </Container>
    </>
  );
}